import {lazy, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {connect, useSelector} from "react-redux";
import {matchPath, useLocation} from "react-router";
import {css} from "@panda/css";
import lscache from "lscache";
import {bindActionCreators, Dispatch} from "redux";

import {Button} from "@pg-design/button-module";
import {CenterBox, Hidden, WideContent} from "@pg-design/grid-module";
import {BrandBuildingCubeIcon, BrandMapPinIcon} from "@pg-design/icons-module";
import {Loader} from "@pg-design/loader-module";
import {Text} from "@pg-design/text-module";
import {useChangeVisibility, useIsMounted} from "@pg-mono/hooks";
import {omit} from "@pg-mono/nodash";
import {rpAppPath} from "@pg-mono/rp-routes";
import {useUserDevice} from "@pg-mono/user-device";

import {rpCommonUrl, rpFeatureFlagVendorReportDownload} from "../../app/read_rp_environment_variables";
import {IRPStore} from "../../app/rp_reducer";
import {ApplicationSource} from "../../application/utils/ApplicationSource";
import {RelatedArticlesSection} from "../../article/components/RelatedArticlesSection";
import {Heading} from "../../atoms/Heading";
import {LocationLinks} from "../../atoms/LocationLinks";
import {IBoosterCategory} from "../../boosters/types/IBoosterCategory";
import {IBoosterClickHandlerParams} from "../../boosters/types/IBoosterClickHandlerParams";
import {FavouritesInfoModal} from "../../favorite/components/FavouritesInfoModal/FavouritesInfoModal";
import {useShouldHidePrices} from "../../hooks/use_should_hide_prices";
import {useOfferBuildings} from "../../hooks/useOfferBuildings";
import {HotlineModal} from "../../hotline/components/HotlineModal";
import {HOTLINE_CHATBOT_MODAL_TRIGGER_ID} from "../../hotline/constants/hotline_chatbot_modal_trigger";
import {HOTLINE_CHATBOT_OFFER_PHONE_NUMBER} from "../../hotline/constants/hotline_phone_numbers";
import {LeadModal} from "../../lead/components/lead_modal/LeadModal";
import {GTMEventApplicationSourceSection} from "../../lead/types/GTMEvenApplicationSourceSection";
import {backToUrl, IBackToUrlAction} from "../../navigation/actions/back_to_url_actions";
import {Country} from "../../region/types/Country";
import {offerDetailPageview} from "../../tracking/algolytics_hits/offer_detail_pageview";
import {gtmOfferGalleryEvents} from "../../tracking/google_tag_manager/gtm_event_offer_gallery_modal";
import {gtmInvestDetailOnOfferDetail} from "../../tracking/google_tag_manager/gtm_invest_detail";
import {useUserPreferencesQueryParams} from "../../user/hooks/use_user_preferences_query_params";
import {getUserPreferencesToOmit} from "../../user/utils/get_user_preferences_to_omit";
import {scrollToElementById} from "../../utils/scroll_to_element_by_id";
import {IVariables} from "../../variables/reducers/variables_reducer";
import {VendorDetailReportModal} from "../../vendor/components/detail/report/VendorDetailReportModal";
import {VendorReportButton} from "../../vendor/components/VendorReportButton";
import {ViewType} from "../../view_type/ViewType";
import {OfferAndPropertyBreadcrumbs} from "../atoms/OfferAndPropertyBreadcrumbs";
import {IRecommendedRegion} from "../atoms/OfferLocationLinksRecommendedRegions";
import {OfferLocationLinksWrapper} from "../atoms/OfferLocationLinksWrapper";
import {DetailConstructionGalleryCTA} from "../components/DetailConstructionGalleryCTA";
import {GalleryHolder} from "../components/gallery_holder/GalleryHolder";
import {NearbyOffersMap} from "../components/NearbyOffersMap";
import {RelatedOfferCarouselWithModal} from "../components/offer_carousel/RelatedOfferCarouselWithModal";
import {OfferDetailHeaderHiddenPriceVariant} from "../components/offer_detail_header/OfferDetailHeaderHiddenPriceVariant";
import {IOfferDetailPromotion} from "../detail/actions/fetch_offer_detail_promotions";
import {OfferDetailForSale} from "../detail/components/OfferDetailForSale";
import {offerDescriptionSectionHeader, OfferDetailLimited} from "../detail/components/OfferDetailLimited";
import {OfferDetailStages} from "../detail/components/stages/OfferDetailStages";
import {Planner3dModal} from "../detail/planner3D/components/Planner3D";
import {SchemaOfferDetail} from "../detail/schema/SchemaOfferDetail";
import {SchemaOfferDetailRating} from "../detail/schema/SchemaOfferDetailRating";
import {buildFriendlyOfferListLink} from "../helpers/friendly_offer_list/build_friendly_offer_list_link";
import {OfferDisplayType} from "../helpers/OfferDisplayType";
import {IOfferArticle} from "../list/actions/fetch_offer_list_articles";
import {IRelatedOfferList} from "../list/types/IOfferListResponse";
import {offerBoxTracking} from "../tracking/offer_box_gtm_events";
import {IOfferDetail} from "../types/IOfferDetail";
import {OfferModalActiveTab} from "../types/OfferModalActiveTab";
import {OfferType} from "../types/OfferType";
import {getOfferDisplayStatus} from "../utils/get_offer_display_status";
import {isOfferArchived} from "../utils/is_offer_archived";
import {offerPlural} from "../utils/offer_plural";

const HotlineChatBotModal = lazy(() => import("../../hotline/components/HotlineChatBotModal"));

interface IStateProps {
    offer: IOfferDetail | null;
    articles: IOfferArticle[];
    recommendedRegions: IRecommendedRegion[];
    relatedOffers: IRelatedOfferList[];
    promotions: IOfferDetailPromotion[];
    variables: IVariables | null;
}

interface IActionsProps {
    backToUrl: (url: string, text: string) => IBackToUrlAction;
}

interface IOwnProps {}

interface IProps extends IStateProps, IActionsProps, IOwnProps {}

const OfferDetailViewC = (props: IProps) => {
    const {offer, promotions, variables, relatedOffers, recommendedRegions, articles, backToUrl} = props;

    const viewType = useSelector((store: IRPStore) => store.viewType.current);
    const isGTMInitialized = useSelector((store: IRPStore) => store.googleTagManager);

    const {isMobile} = useUserDevice();
    const isMounted = useIsMounted();
    const {shouldHidePrices} = useShouldHidePrices();

    const userPreferencesQueryParams = useUserPreferencesQueryParams();

    const getFriendlyOfferListLink = () => {
        const isFlat = offer?.type === OfferType.FLAT;
        const stats = offer?.region?.stats;

        const isEmpty = isFlat ? (stats?.properties_flats_count_for_sale ?? 0) <= 0 : (stats?.properties_houses_count_for_sale ?? 0) <= 0;

        const regionSlug = isEmpty ? offer?.region?.parent?.slug : offer?.region?.slug;
        const country = offer?.region?.country;
        const type = isFlat ? OfferType.FLAT : OfferType.HOUSE;

        const clearedPreferences = omit(userPreferencesQueryParams, getUserPreferencesToOmit(offer?.type));

        return buildFriendlyOfferListLink({type, region: regionSlug, country: country === Country.POLAND ? undefined : country, ...clearedPreferences});
    };

    const regionName =
        offer !== null &&
        (offer?.type === OfferType.FLAT
            ? offer.region.stats && offer.region.stats.properties_flats_count_for_sale <= 0
                ? offer.region.parent.name
                : offer.region.name
            : offer.region.stats && offer.region.stats.properties_houses_count_for_sale <= 0
              ? offer.region.parent.name
              : offer.region.name);

    useEffect(() => {
        if (offer) {
            offerDetailPageview(offer, offer.vendor);
        }

        const prevUrl = document.referrer;

        if (prevUrl !== "") {
            const url = new URL(prevUrl);
            const baseUrl = `${url.protocol}//${url.host}`;
            if (baseUrl === rpCommonUrl && backToPaths.some((path: string) => matchPath(url.pathname, {path: path, exact: true, strict: true}) !== null)) {
                backToUrl(prevUrl, "OFERTY");
                return;
            }
        }

        backToUrl(getFriendlyOfferListLink(), `oferty ${regionName}`);
    }, [offer, userPreferencesQueryParams]);

    /*
    This particular event has to be fired only once. Event checks if gtm is initialized and offer data is available.
    Because of SSR offer is already available and dependency in useEffect is set to GTM flag from store.
    This is crucial for the event to fire in correct order.
     */
    useEffect(() => {
        if (offer && isGTMInitialized) {
            gtmInvestDetailOnOfferDetail(offer);
        }
    }, [isGTMInitialized]);

    const [isModalAndTabActive, _setIsModalAndTabActive] = useState<OfferModalActiveTab | null>(null);
    const setIsModalAndTabActive = (tab: OfferModalActiveTab | null, source?: GTMEventApplicationSourceSection | null) => {
        if (tab && isModalAndTabActive === null) {
            // send this GTM event only when opening modal
            gtmOfferGalleryEvents.onOfferGalleryModalOpen(tab, viewType, source ?? null);
        }
        _setIsModalAndTabActive(tab);
    };

    const isAbroad = offer?.region.country !== Country.POLAND;
    const backToPaths = [rpAppPath.friendlyOfferList, rpAppPath.multiOfferList.base, rpAppPath.offer.list];
    const isForSale = offer?.configuration.display_type === OfferDisplayType.FOR_SALE;
    const isPlanned = offer?.configuration.display_type === OfferDisplayType.PLANNED;
    const isSold = offer?.configuration.display_type === OfferDisplayType.SOLD;
    const isLimited = offer && getOfferDisplayStatus(offer) === "limited";
    const isArchived = offer && isOfferArchived(offer);
    const isClassicView = isForSale && !isLimited;
    const regionStatsForSale =
        (offer?.type === OfferType.FLAT
            ? offer.region.stats && offer.region.stats.properties_flats_count_for_sale <= 0
                ? offer.region.parent.stats && offer.region.parent.stats.properties_flats_count_for_sale
                : offer.region.stats?.properties_flats_count_for_sale
            : offer !== null && offer.region.stats && offer.region.stats.properties_houses_count_for_sale <= 0
              ? offer.region.parent.stats && offer.region.parent.stats.properties_houses_count_for_sale
              : offer !== null && offer.region.stats && offer.region.stats.properties_houses_count_for_sale) || false;

    const refGallery = useRef<HTMLTableSectionElement>(null);
    const refProperties = useRef<HTMLTableSectionElement>(null);
    const refPromotions = useRef<HTMLTableSectionElement>(null);
    const refDescription = useRef<HTMLTableSectionElement>(null);
    const refLocation = useRef<HTMLTableSectionElement>(null);
    const refContact = useRef<HTMLTableSectionElement>(null);
    const refElems = useMemo(
        () => ({
            refGallery,
            refProperties,
            refDescription,
            refPromotions,
            refLocation,
            refContact
        }),
        []
    );

    const hasProperties = offer != null ? (offer.stats?.properties_count_for_sale ?? 0) > 0 : false;
    const hasPromotions = promotions.length > 0;
    const hasDescription = offer != null ? !!(offer.description || offer.configuration.show_generated_description) : false;

    const {offerBuildings, fetchOfferBuildings} = useOfferBuildings();
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const location = useLocation();

    const currentStageOfferCount = offer?.groups?.stages.filter((stage) => stage.current);

    useEffect(() => {
        if (!!isModalAndTabActive) {
            setModalIsOpen(true);
        }
    }, [isModalAndTabActive]);

    useEffect(() => {
        if (!modalIsOpen) {
            setIsModalAndTabActive(null);
        }
    }, [modalIsOpen]);

    useEffect(() => {
        lscache.flushExpired();
    }, []);

    const changeModalTab = useCallback(
        (tab: OfferModalActiveTab, source?: GTMEventApplicationSourceSection): void => {
            setIsModalAndTabActive(tab, source);

            offer?.configuration.show_building_models_on_map && fetchOfferBuildings(offer?.id);
        },
        [offer, fetchOfferBuildings]
    );

    // Scroll and visibility solution
    const getElems = useCallback(() => {
        const res = [];
        if (hasProperties) {
            res.push("properties");
        }
        if (hasPromotions) {
            res.push("promotions");
        }
        if (hasDescription) {
            res.push("description");
        }
        res.push("location");
        res.push("contact");
        return res;
    }, [hasProperties, hasPromotions, hasDescription]);

    const [visibleElem, changeVisibility] = useChangeVisibility(getElems, 100);

    /**
     * Render
     */

    if (offer == null) {
        return (
            <CenterBox height="500px">
                <Loader size="lg" />
            </CenterBox>
        );
    }
    const {region_type_voivodeship, region_type_city, region_type_county, region_type_district, region_type_housing_estate, region_type_town} =
        offer.region.stats || {};

    const breadcrumbs = {
        region_type_voivodeship: region_type_voivodeship || null,
        region_type_city: region_type_city || null,
        region_type_county: region_type_county || null,
        region_type_district: region_type_district || null,
        region_type_housing_estate: region_type_housing_estate || null,
        region_type_town: region_type_town || null
    };
    const {vendor} = offer;

    const handleBoosterClick = (params: IBoosterClickHandlerParams) => {
        if (params.category === IBoosterCategory.Promotion) {
            scrollToElementById(document.documentElement, "promocje", 150);
        }
    };

    const newHeaderVariant = shouldHidePrices ? "blur" : "visible";

    return (
        <>
            <article>
                <section>
                    <WideContent className={css({mt: 2})}>
                        <OfferAndPropertyBreadcrumbs
                            country={offer.region.country.toString()}
                            type={offer.type}
                            breadcrumbs={breadcrumbs}
                            offer={{name: offer.name, slug: offer.slug, id: offer.id}}
                            groups={offer.groups}
                            vendor={{name: vendor.name, id: vendor.id, slug: vendor.slug}}
                        />
                    </WideContent>
                </section>

                <WideContent className={headerWrapper}>
                    <OfferDetailHeaderHiddenPriceVariant
                        offer={offer}
                        variant={newHeaderVariant}
                        promotions={promotions}
                        onBoosterClick={handleBoosterClick}
                        onLocationClick={() => setIsModalAndTabActive(OfferModalActiveTab.MAP, GTMEventApplicationSourceSection.HEADER)}
                    />
                </WideContent>

                {isPlanned && !isArchived && (
                    <WideContent>
                        <div className={mainMessageWrap}>
                            <Text className={mainMessageTitleStyle}>Inwestycja planowana</Text>

                            <Text variant="body_copy_2">
                                Uwaga! Deweloper nie rozpoczął jeszcze sprzedaży mieszkań w tej inwestycji. {regionStatsForSale && `Mamy dla Ciebie`}{" "}
                                {regionStatsForSale && (
                                    <a className={noWrapUnderlinedStyle} href={getFriendlyOfferListLink()}>
                                        {regionStatsForSale} {offerPlural(regionStatsForSale)}
                                    </a>
                                )}{" "}
                                {regionStatsForSale && `w\xa0najbliższej okolicy!`}
                            </Text>

                            {isSold && (
                                <>
                                    <InactiveInfo />
                                    <NearestOffersInfo regionStatsForSale={regionStatsForSale} friendlyOfferListLink={getFriendlyOfferListLink()} />
                                </>
                            )}
                        </div>
                    </WideContent>
                )}

                {isArchived && !isPlanned && (
                    <>
                        <WideContent>
                            <div className={mainMessageWrap}>
                                <Text className={mainMessageTitleStyle}>Oferta archiwalna</Text>

                                {(isSold || isLimited) && (
                                    <>
                                        <InactiveInfo />
                                        <NearestOffersInfo
                                            regionStatsForSale={regionStatsForSale}
                                            friendlyOfferListLink={
                                                currentStageOfferCount && currentStageOfferCount.length > 0 ? location.pathname : getFriendlyOfferListLink()
                                            }
                                            currentStageOfferCount={
                                                currentStageOfferCount && currentStageOfferCount.length > 0
                                                    ? currentStageOfferCount[0].offer.stats.properties_count_for_sale
                                                    : null
                                            }
                                        />
                                    </>
                                )}
                            </div>
                        </WideContent>

                        <OfferDetailStages offer={offer} isArchived />
                    </>
                )}

                {!isClassicView && (
                    <WideContent>
                        {relatedOffers.length > 0 && (
                            <>
                                <Heading type="h2" className={offerDescriptionSectionHeader}>
                                    {isArchived ? "Podobne oferty w najbliższej okolicy" : "Najbliższe inwestycje"}
                                </Heading>

                                {isArchived && (
                                    <RelatedOfferCarouselWithModal
                                        offerIds={relatedOffers.map((offer) => offer.id)}
                                        gtmMeta={
                                            isArchived
                                                ? {
                                                      listName: "Podobne oferty archiwalne w najbliższej okolicy",
                                                      listId: "podobne_oferty_archiwalne_w_najblizszej_okolicy",
                                                      source: "additional",
                                                      tileEvents: {
                                                          imageClick: offerBoxTracking.gtm.investAdditionalImageClick,
                                                          buttonClick: offerBoxTracking.gtm.investAdditionalButtonClick,
                                                          linkClick: offerBoxTracking.gtm.investAdditionalLinkClick,
                                                          logoClick: offerBoxTracking.gtm.investAdditionalLogoClick
                                                      }
                                                  }
                                                : {
                                                      listName: "Najbliższe inwestycje",
                                                      listId: "najblizsze_inwestycje",
                                                      source: "additional",
                                                      tileEvents: {
                                                          imageClick: offerBoxTracking.gtm.investAdditionalImageClick,
                                                          buttonClick: offerBoxTracking.gtm.investAdditionalButtonClick,
                                                          linkClick: offerBoxTracking.gtm.investAdditionalLinkClick,
                                                          logoClick: offerBoxTracking.gtm.investAdditionalLogoClick
                                                      }
                                                  }
                                        }
                                    />
                                )}

                                <div className={mapWrapper}>
                                    <NearbyOffersMap
                                        viewType={ViewType.OFFER}
                                        originCountry={offer.region.country}
                                        originCoordinates={offer.geo_point.coordinates}
                                        offers={relatedOffers}
                                    />
                                </div>
                            </>
                        )}

                        {(isSold || (isLimited && !isPlanned)) && (
                            <Heading type="h2" className={offerDescriptionSectionHeader}>
                                Prezentacja oferty archiwalnej
                            </Heading>
                        )}
                    </WideContent>
                )}

                <section id="galeria" ref={refElems.refGallery}>
                    <GalleryHolder
                        source={ApplicationSource.Offer}
                        offer={offer}
                        offerBuildings={offerBuildings}
                        isOpen={modalIsOpen}
                        handleGalleryModalChange={(tab) => setIsModalAndTabActive(tab, GTMEventApplicationSourceSection.IMAGE)}
                        modalState={isModalAndTabActive}
                        onModalClose={() => {
                            setModalIsOpen(false);
                        }}
                    />
                </section>

                <Hidden on="desktop">
                    <WideContent className={mobileIconButtonsWrapper}>
                        {rpFeatureFlagVendorReportDownload && !isAbroad && isMobile && <VendorReportButton vendorId={offer.vendor.id} />}

                        <Button
                            size="medium"
                            variant="brand_icon"
                            iconLeft={BrandMapPinIcon}
                            onClick={() => setIsModalAndTabActive(OfferModalActiveTab.MAP, GTMEventApplicationSourceSection.BUTTON)}
                            className={css({m: 0, p: 0})}
                        >
                            Zobacz na mapie
                        </Button>

                        <DetailConstructionGalleryCTA
                            onButtonClick={() => setIsModalAndTabActive(OfferModalActiveTab.CONSTRUCTION_GALLERY, GTMEventApplicationSourceSection.BUTTON)}
                        />

                        <Planner3dModal offer={offer} vendorId={offer.vendor.id} className={planner3DStyle} routedModalId="view">
                            <Button iconLeft={BrandBuildingCubeIcon} variant="brand_icon" size={isMobile ? "medium" : "big"} className={css({mb: 2, pl: 0})}>
                                Plan pięter
                            </Button>
                        </Planner3dModal>
                    </WideContent>
                </Hidden>

                {isClassicView ? (
                    <OfferDetailForSale
                        offer={offer}
                        promotions={promotions}
                        variables={variables}
                        refElems={refElems}
                        visibleElement={visibleElem}
                        changeVisibility={changeVisibility}
                        changeModalTab={changeModalTab}
                        hasProperties={hasProperties}
                        hasDescription={hasDescription}
                        shouldMapBeRendered={!modalIsOpen}
                    />
                ) : (
                    <OfferDetailLimited
                        offer={offer}
                        refElems={refElems}
                        variables={variables}
                        changeModalTab={changeModalTab}
                        hasDescription={hasDescription}
                        shouldMapBeRendered={!modalIsOpen}
                    />
                )}

                {articles.length > 0 && <RelatedArticlesSection heading="Artykuły powiązane:" articles={articles} />}

                <SchemaOfferDetail offer={offer} />

                {!isSold && offer.is_active && <SchemaOfferDetailRating offer={offer} />}

                {/* For now, we don't want to show abroad regions on offers listing view - so we don't want to show links to listings */}
                {offer && offer.region.country === Country.POLAND && (
                    <OfferLocationLinksWrapper className={linksWrapperStyle}>
                        <LocationLinks recommendedRegions={recommendedRegions} offerType={offer.type} region={offer.region} relatedOffers={relatedOffers} />
                    </OfferLocationLinksWrapper>
                )}
            </article>

            {/** View Modals */}
            <FavouritesInfoModal />

            <LeadModal />

            {isMounted && <HotlineChatBotModal hotlineNumber={HOTLINE_CHATBOT_OFFER_PHONE_NUMBER} hotlineModalTriggerId={HOTLINE_CHATBOT_MODAL_TRIGGER_ID} />}

            {!isArchived && <HotlineModal />}

            {rpFeatureFlagVendorReportDownload && !isAbroad && <VendorDetailReportModal />}
        </>
    );
};

export const OfferDetailView = connect<IStateProps, IActionsProps, IOwnProps, IRPStore>(mapStateToProps, mapActionsToProps)(OfferDetailViewC);

/**
 * Connect
 */
function mapStateToProps(state: IRPStore): IStateProps {
    return {
        offer: state.offerDetail.offer,
        articles: state.offerDetail.articles,
        promotions: state.offerDetail.promotions,
        variables: state.variables && state.variables.data,
        recommendedRegions: state.offerDetail.recommendedRegions,
        relatedOffers: state.offerDetail.relatedOffers
    };
}

function mapActionsToProps(dispatch: Dispatch): IActionsProps {
    return {
        ...bindActionCreators({backToUrl}, dispatch)
    };
}

// Sub components
interface INearestOffersInfoProps {
    friendlyOfferListLink: string;
    regionStatsForSale?: number | false;
    currentStageOfferCount?: number | null;
}

function NearestOffersInfo(props: INearestOffersInfoProps) {
    const {regionStatsForSale, friendlyOfferListLink, currentStageOfferCount} = props;

    const saleStats = currentStageOfferCount ? currentStageOfferCount : regionStatsForSale;

    if (!saleStats) {
        return;
    }

    return (
        <Text variant="body_copy_2" className={css({pt: 2})}>
            {`Mamy dla Ciebie `}
            <a className={noWrapUnderlinedStyle} href={friendlyOfferListLink}>
                {saleStats} {offerPlural(saleStats)}
            </a>

            {currentStageOfferCount ? ` w kolejnym etapie tej inwestycji!` : " w najbliższej okolicy!"}
        </Text>
    );
}

function InactiveInfo() {
    return (
        <Text variant="body_copy_2">
            Uwaga! Wizytówka niniejszej inwestycji jest nieaktywna. Informacje na jej temat pomagają nam jednak tworzyć wiarygodną bazę informacji na temat
            lokalnego rynku nieruchomości.
        </Text>
    );
}

//  Styles

const headerWrapper = css({
    my: 2,

    lg: {
        mt: 3,
        mb: 2
    }
});

const linksWrapperStyle = css({
    p: 0,
    backgroundColor: "#fff",

    md: {
        py: "6.4rem !important"
    }
});

const mainMessageWrap = css({
    mb: 2,
    borderRadius: 2,
    p: 2,
    background: "#fff",
    boxShadow: "var(--shadow.0)"
});

const mainMessageTitleStyle = css({
    mb: 2
});

const noWrapUnderlinedStyle = css({
    textWrap: "unset",
    textDecoration: "underline"
});

const mapWrapper = css({
    mb: 3,
    position: "relative",
    width: "100%",
    height: "500px",

    sm: {
        height: "600px"
    }
});

const planner3DStyle = css({
    display: "inline-block"
});

const mobileIconButtonsWrapper = css({
    display: "flex",
    flexWrap: "wrap",
    columnGap: "2.7rem",
    rowGap: 2,
    mb: 2
});
import {lazy, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {connect, useSelector} from "react-redux";
import {matchPath, useLocation} from "react-router";
import {css} from "@panda/css";
import lscache from "lscache";
import {bindActionCreators, Dispatch} from "redux";

import {Button} from "@pg-design/button-module";
import {CenterBox, Hidden, WideContent} from "@pg-design/grid-module";
import {BrandBuildingCubeIcon, BrandMapPinIcon} from "@pg-design/icons-module";
import {Loader} from "@pg-design/loader-module";
import {Text} from "@pg-design/text-module";
import {useChangeVisibility, useIsMounted} from "@pg-mono/hooks";
import {omit} from "@pg-mono/nodash";
import {rpAppPath} from "@pg-mono/rp-routes";
import {useUserDevice} from "@pg-mono/user-device";

import {rpCommonUrl, rpFeatureFlagVendorReportDownload} from "../../app/read_rp_environment_variables";
import {IRPStore} from "../../app/rp_reducer";
import {ApplicationSource} from "../../application/utils/ApplicationSource";
import {RelatedArticlesSection} from "../../article/components/RelatedArticlesSection";
import {Heading} from "../../atoms/Heading";
import {LocationLinks} from "../../atoms/LocationLinks";
import {IBoosterCategory} from "../../boosters/types/IBoosterCategory";
import {IBoosterClickHandlerParams} from "../../boosters/types/IBoosterClickHandlerParams";
import {FavouritesInfoModal} from "../../favorite/components/FavouritesInfoModal/FavouritesInfoModal";
import {useShouldHidePrices} from "../../hooks/use_should_hide_prices";
import {useOfferBuildings} from "../../hooks/useOfferBuildings";
import {HotlineModal} from "../../hotline/components/HotlineModal";
import {HOTLINE_CHATBOT_MODAL_TRIGGER_ID} from "../../hotline/constants/hotline_chatbot_modal_trigger";
import {HOTLINE_CHATBOT_OFFER_PHONE_NUMBER} from "../../hotline/constants/hotline_phone_numbers";
import {LeadModal} from "../../lead/components/lead_modal/LeadModal";
import {GTMEventApplicationSourceSection} from "../../lead/types/GTMEvenApplicationSourceSection";
import {backToUrl, IBackToUrlAction} from "../../navigation/actions/back_to_url_actions";
import {Country} from "../../region/types/Country";
import {offerDetailPageview} from "../../tracking/algolytics_hits/offer_detail_pageview";
import {gtmOfferGalleryEvents} from "../../tracking/google_tag_manager/gtm_event_offer_gallery_modal";
import {gtmInvestDetailOnOfferDetail} from "../../tracking/google_tag_manager/gtm_invest_detail";
import {useUserPreferencesQueryParams} from "../../user/hooks/use_user_preferences_query_params";
import {getUserPreferencesToOmit} from "../../user/utils/get_user_preferences_to_omit";
import {scrollToElementById} from "../../utils/scroll_to_element_by_id";
import {IVariables} from "../../variables/reducers/variables_reducer";
import {VendorDetailReportModal} from "../../vendor/components/detail/report/VendorDetailReportModal";
import {VendorReportButton} from "../../vendor/components/VendorReportButton";
import {ViewType} from "../../view_type/ViewType";
import {OfferAndPropertyBreadcrumbs} from "../atoms/OfferAndPropertyBreadcrumbs";
import {IRecommendedRegion} from "../atoms/OfferLocationLinksRecommendedRegions";
import {OfferLocationLinksWrapper} from "../atoms/OfferLocationLinksWrapper";
import {DetailConstructionGalleryCTA} from "../components/DetailConstructionGalleryCTA";
import {GalleryHolder} from "../components/gallery_holder/GalleryHolder";
import {NearbyOffersMap} from "../components/NearbyOffersMap";
import {RelatedOfferCarouselWithModal} from "../components/offer_carousel/RelatedOfferCarouselWithModal";
import {OfferDetailHeaderHiddenPriceVariant} from "../components/offer_detail_header/OfferDetailHeaderHiddenPriceVariant";
import {IOfferDetailPromotion} from "../detail/actions/fetch_offer_detail_promotions";
import {OfferDetailForSale} from "../detail/components/OfferDetailForSale";
import {offerDescriptionSectionHeader, OfferDetailLimited} from "../detail/components/OfferDetailLimited";
import {OfferDetailStages} from "../detail/components/stages/OfferDetailStages";
import {Planner3dModal} from "../detail/planner3D/components/Planner3D";
import {SchemaOfferDetail} from "../detail/schema/SchemaOfferDetail";
import {SchemaOfferDetailRating} from "../detail/schema/SchemaOfferDetailRating";
import {buildFriendlyOfferListLink} from "../helpers/friendly_offer_list/build_friendly_offer_list_link";
import {OfferDisplayType} from "../helpers/OfferDisplayType";
import {IOfferArticle} from "../list/actions/fetch_offer_list_articles";
import {IRelatedOfferList} from "../list/types/IOfferListResponse";
import {offerBoxTracking} from "../tracking/offer_box_gtm_events";
import {IOfferDetail} from "../types/IOfferDetail";
import {OfferModalActiveTab} from "../types/OfferModalActiveTab";
import {OfferType} from "../types/OfferType";
import {getOfferDisplayStatus} from "../utils/get_offer_display_status";
import {isOfferArchived} from "../utils/is_offer_archived";
import {offerPlural} from "../utils/offer_plural";

const HotlineChatBotModal = lazy(() => import("../../hotline/components/HotlineChatBotModal"));

interface IStateProps {
    offer: IOfferDetail | null;
    articles: IOfferArticle[];
    recommendedRegions: IRecommendedRegion[];
    relatedOffers: IRelatedOfferList[];
    promotions: IOfferDetailPromotion[];
    variables: IVariables | null;
}

interface IActionsProps {
    backToUrl: (url: string, text: string) => IBackToUrlAction;
}

interface IOwnProps {}

interface IProps extends IStateProps, IActionsProps, IOwnProps {}

const OfferDetailViewC = (props: IProps) => {
    const {offer, promotions, variables, relatedOffers, recommendedRegions, articles, backToUrl} = props;

    const viewType = useSelector((store: IRPStore) => store.viewType.current);
    const isGTMInitialized = useSelector((store: IRPStore) => store.googleTagManager);

    const {isMobile} = useUserDevice();
    const isMounted = useIsMounted();
    const {shouldHidePrices} = useShouldHidePrices();

    const userPreferencesQueryParams = useUserPreferencesQueryParams();

    const getFriendlyOfferListLink = () => {
        const isFlat = offer?.type === OfferType.FLAT;
        const stats = offer?.region?.stats;

        const isEmpty = isFlat ? (stats?.properties_flats_count_for_sale ?? 0) <= 0 : (stats?.properties_houses_count_for_sale ?? 0) <= 0;

        const regionSlug = isEmpty ? offer?.region?.parent?.slug : offer?.region?.slug;
        const country = offer?.region?.country;
        const type = isFlat ? OfferType.FLAT : OfferType.HOUSE;

        const clearedPreferences = omit(userPreferencesQueryParams, getUserPreferencesToOmit(offer?.type));

        return buildFriendlyOfferListLink({type, region: regionSlug, country: country === Country.POLAND ? undefined : country, ...clearedPreferences});
    };

    const regionName =
        offer !== null &&
        (offer?.type === OfferType.FLAT
            ? offer.region.stats && offer.region.stats.properties_flats_count_for_sale <= 0
                ? offer.region.parent.name
                : offer.region.name
            : offer.region.stats && offer.region.stats.properties_houses_count_for_sale <= 0
              ? offer.region.parent.name
              : offer.region.name);

    useEffect(() => {
        if (offer) {
            offerDetailPageview(offer, offer.vendor);
        }

        const prevUrl = document.referrer;

        if (prevUrl !== "") {
            const url = new URL(prevUrl);
            const baseUrl = `${url.protocol}//${url.host}`;
            if (baseUrl === rpCommonUrl && backToPaths.some((path: string) => matchPath(url.pathname, {path: path, exact: true, strict: true}) !== null)) {
                backToUrl(prevUrl, "OFERTY");
                return;
            }
        }

        backToUrl(getFriendlyOfferListLink(), `oferty ${regionName}`);
    }, [offer, userPreferencesQueryParams]);

    /*
    This particular event has to be fired only once. Event checks if gtm is initialized and offer data is available.
    Because of SSR offer is already available and dependency in useEffect is set to GTM flag from store.
    This is crucial for the event to fire in correct order.
     */
    useEffect(() => {
        if (offer && isGTMInitialized) {
            gtmInvestDetailOnOfferDetail(offer);
        }
    }, [isGTMInitialized]);

    const [isModalAndTabActive, _setIsModalAndTabActive] = useState<OfferModalActiveTab | null>(null);
    const setIsModalAndTabActive = (tab: OfferModalActiveTab | null, source?: GTMEventApplicationSourceSection | null) => {
        if (tab && isModalAndTabActive === null) {
            // send this GTM event only when opening modal
            gtmOfferGalleryEvents.onOfferGalleryModalOpen(tab, viewType, source ?? null);
        }
        _setIsModalAndTabActive(tab);
    };

    const isAbroad = offer?.region.country !== Country.POLAND;
    const backToPaths = [rpAppPath.friendlyOfferList, rpAppPath.multiOfferList.base, rpAppPath.offer.list];
    const isForSale = offer?.configuration.display_type === OfferDisplayType.FOR_SALE;
    const isPlanned = offer?.configuration.display_type === OfferDisplayType.PLANNED;
    const isSold = offer?.configuration.display_type === OfferDisplayType.SOLD;
    const isLimited = offer && getOfferDisplayStatus(offer) === "limited";
    const isArchived = offer && isOfferArchived(offer);
    const isClassicView = isForSale && !isLimited;
    const regionStatsForSale =
        (offer?.type === OfferType.FLAT
            ? offer.region.stats && offer.region.stats.properties_flats_count_for_sale <= 0
                ? offer.region.parent.stats && offer.region.parent.stats.properties_flats_count_for_sale
                : offer.region.stats?.properties_flats_count_for_sale
            : offer !== null && offer.region.stats && offer.region.stats.properties_houses_count_for_sale <= 0
              ? offer.region.parent.stats && offer.region.parent.stats.properties_houses_count_for_sale
              : offer !== null && offer.region.stats && offer.region.stats.properties_houses_count_for_sale) || false;

    const refGallery = useRef<HTMLTableSectionElement>(null);
    const refProperties = useRef<HTMLTableSectionElement>(null);
    const refPromotions = useRef<HTMLTableSectionElement>(null);
    const refDescription = useRef<HTMLTableSectionElement>(null);
    const refLocation = useRef<HTMLTableSectionElement>(null);
    const refContact = useRef<HTMLTableSectionElement>(null);
    const refElems = useMemo(
        () => ({
            refGallery,
            refProperties,
            refDescription,
            refPromotions,
            refLocation,
            refContact
        }),
        []
    );

    const hasProperties = offer != null ? (offer.stats?.properties_count_for_sale ?? 0) > 0 : false;
    const hasPromotions = promotions.length > 0;
    const hasDescription = offer != null ? !!(offer.description || offer.configuration.show_generated_description) : false;

    const {offerBuildings, fetchOfferBuildings} = useOfferBuildings();
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const location = useLocation();

    const currentStageOfferCount = offer?.groups?.stages.filter((stage) => stage.current);

    useEffect(() => {
        if (!!isModalAndTabActive) {
            setModalIsOpen(true);
        }
    }, [isModalAndTabActive]);

    useEffect(() => {
        if (!modalIsOpen) {
            setIsModalAndTabActive(null);
        }
    }, [modalIsOpen]);

    useEffect(() => {
        lscache.flushExpired();
    }, []);

    const changeModalTab = useCallback(
        (tab: OfferModalActiveTab, source?: GTMEventApplicationSourceSection): void => {
            setIsModalAndTabActive(tab, source);

            offer?.configuration.show_building_models_on_map && fetchOfferBuildings(offer?.id);
        },
        [offer, fetchOfferBuildings]
    );

    // Scroll and visibility solution
    const getElems = useCallback(() => {
        const res = [];
        if (hasProperties) {
            res.push("properties");
        }
        if (hasPromotions) {
            res.push("promotions");
        }
        if (hasDescription) {
            res.push("description");
        }
        res.push("location");
        res.push("contact");
        return res;
    }, [hasProperties, hasPromotions, hasDescription]);

    const [visibleElem, changeVisibility] = useChangeVisibility(getElems, 100);

    /**
     * Render
     */

    if (offer == null) {
        return (
            <CenterBox height="500px">
                <Loader size="lg" />
            </CenterBox>
        );
    }
    const {region_type_voivodeship, region_type_city, region_type_county, region_type_district, region_type_housing_estate, region_type_town} =
        offer.region.stats || {};

    const breadcrumbs = {
        region_type_voivodeship: region_type_voivodeship || null,
        region_type_city: region_type_city || null,
        region_type_county: region_type_county || null,
        region_type_district: region_type_district || null,
        region_type_housing_estate: region_type_housing_estate || null,
        region_type_town: region_type_town || null
    };
    const {vendor} = offer;

    const handleBoosterClick = (params: IBoosterClickHandlerParams) => {
        if (params.category === IBoosterCategory.Promotion) {
            scrollToElementById(document.documentElement, "promocje", 150);
        }
    };

    const newHeaderVariant = shouldHidePrices ? "blur" : "visible";

    return (
        <>
            <article>
                <section>
                    <WideContent className={css({mt: 2})}>
                        <OfferAndPropertyBreadcrumbs
                            country={offer.region.country.toString()}
                            type={offer.type}
                            breadcrumbs={breadcrumbs}
                            offer={{name: offer.name, slug: offer.slug, id: offer.id}}
                            groups={offer.groups}
                            vendor={{name: vendor.name, id: vendor.id, slug: vendor.slug}}
                        />
                    </WideContent>
                </section>

                <WideContent className={headerWrapper}>
                    <OfferDetailHeaderHiddenPriceVariant
                        offer={offer}
                        variant={newHeaderVariant}
                        promotions={promotions}
                        onBoosterClick={handleBoosterClick}
                        onLocationClick={() => setIsModalAndTabActive(OfferModalActiveTab.MAP, GTMEventApplicationSourceSection.HEADER)}
                    />
                </WideContent>

                {isPlanned && !isArchived && (
                    <WideContent>
                        <div className={mainMessageWrap}>
                            <Text className={mainMessageTitleStyle}>Inwestycja planowana</Text>

                            <Text variant="body_copy_2">
                                Uwaga! Deweloper nie rozpoczął jeszcze sprzedaży mieszkań w tej inwestycji. {regionStatsForSale && `Mamy dla Ciebie`}{" "}
                                {regionStatsForSale && (
                                    <a className={noWrapUnderlinedStyle} href={getFriendlyOfferListLink()}>
                                        {regionStatsForSale} {offerPlural(regionStatsForSale)}
                                    </a>
                                )}{" "}
                                {regionStatsForSale && `w\xa0najbliższej okolicy!`}
                            </Text>

                            {isSold && (
                                <>
                                    <InactiveInfo />
                                    <NearestOffersInfo regionStatsForSale={regionStatsForSale} friendlyOfferListLink={getFriendlyOfferListLink()} />
                                </>
                            )}
                        </div>
                    </WideContent>
                )}

                {isArchived && !isPlanned && (
                    <>
                        <WideContent>
                            <div className={mainMessageWrap}>
                                <Text className={mainMessageTitleStyle}>Oferta archiwalna</Text>

                                {(isSold || isLimited) && (
                                    <>
                                        <InactiveInfo />
                                        <NearestOffersInfo
                                            regionStatsForSale={regionStatsForSale}
                                            friendlyOfferListLink={
                                                currentStageOfferCount && currentStageOfferCount.length > 0 ? location.pathname : getFriendlyOfferListLink()
                                            }
                                            currentStageOfferCount={
                                                currentStageOfferCount && currentStageOfferCount.length > 0
                                                    ? currentStageOfferCount[0].offer.stats.properties_count_for_sale
                                                    : null
                                            }
                                        />
                                    </>
                                )}
                            </div>
                        </WideContent>

                        <OfferDetailStages offer={offer} isArchived />
                    </>
                )}

                {!isClassicView && (
                    <WideContent>
                        {relatedOffers.length > 0 && (
                            <>
                                <Heading type="h2" className={offerDescriptionSectionHeader}>
                                    {isArchived ? "Podobne oferty w najbliższej okolicy" : "Najbliższe inwestycje"}
                                </Heading>

                                {isArchived && (
                                    <RelatedOfferCarouselWithModal
                                        offerIds={relatedOffers.map((offer) => offer.id)}
                                        gtmMeta={
                                            isArchived
                                                ? {
                                                      listName: "Podobne oferty archiwalne w najbliższej okolicy",
                                                      listId: "podobne_oferty_archiwalne_w_najblizszej_okolicy",
                                                      source: "additional",
                                                      tileEvents: {
                                                          imageClick: offerBoxTracking.gtm.investAdditionalImageClick,
                                                          buttonClick: offerBoxTracking.gtm.investAdditionalButtonClick,
                                                          linkClick: offerBoxTracking.gtm.investAdditionalLinkClick,
                                                          logoClick: offerBoxTracking.gtm.investAdditionalLogoClick
                                                      }
                                                  }
                                                : {
                                                      listName: "Najbliższe inwestycje",
                                                      listId: "najblizsze_inwestycje",
                                                      source: "additional",
                                                      tileEvents: {
                                                          imageClick: offerBoxTracking.gtm.investAdditionalImageClick,
                                                          buttonClick: offerBoxTracking.gtm.investAdditionalButtonClick,
                                                          linkClick: offerBoxTracking.gtm.investAdditionalLinkClick,
                                                          logoClick: offerBoxTracking.gtm.investAdditionalLogoClick
                                                      }
                                                  }
                                        }
                                    />
                                )}

                                <div className={mapWrapper}>
                                    <NearbyOffersMap
                                        viewType={ViewType.OFFER}
                                        originCountry={offer.region.country}
                                        originCoordinates={offer.geo_point.coordinates}
                                        offers={relatedOffers}
                                    />
                                </div>
                            </>
                        )}

                        {(isSold || (isLimited && !isPlanned)) && (
                            <Heading type="h2" className={offerDescriptionSectionHeader}>
                                Prezentacja oferty archiwalnej
                            </Heading>
                        )}
                    </WideContent>
                )}

                <section id="galeria" ref={refElems.refGallery}>
                    <GalleryHolder
                        source={ApplicationSource.Offer}
                        offer={offer}
                        offerBuildings={offerBuildings}
                        isOpen={modalIsOpen}
                        handleGalleryModalChange={(tab) => setIsModalAndTabActive(tab, GTMEventApplicationSourceSection.IMAGE)}
                        modalState={isModalAndTabActive}
                        onModalClose={() => {
                            setModalIsOpen(false);
                        }}
                    />
                </section>

                <Hidden on="desktop">
                    <WideContent className={mobileIconButtonsWrapper}>
                        {rpFeatureFlagVendorReportDownload && !isAbroad && isMobile && <VendorReportButton vendorId={offer.vendor.id} />}

                        <Button
                            size="medium"
                            variant="brand_icon"
                            iconLeft={BrandMapPinIcon}
                            onClick={() => setIsModalAndTabActive(OfferModalActiveTab.MAP, GTMEventApplicationSourceSection.BUTTON)}
                            className={css({m: 0, p: 0})}
                        >
                            Zobacz na mapie
                        </Button>

                        <DetailConstructionGalleryCTA
                            onButtonClick={() => setIsModalAndTabActive(OfferModalActiveTab.CONSTRUCTION_GALLERY, GTMEventApplicationSourceSection.BUTTON)}
                        />

                        <Planner3dModal offer={offer} vendorId={offer.vendor.id} className={planner3DStyle} routedModalId="view">
                            <Button iconLeft={BrandBuildingCubeIcon} variant="brand_icon" size={isMobile ? "medium" : "big"} className={css({mb: 2, pl: 0})}>
                                Plan pięter
                            </Button>
                        </Planner3dModal>
                    </WideContent>
                </Hidden>

                {isClassicView ? (
                    <OfferDetailForSale
                        offer={offer}
                        promotions={promotions}
                        variables={variables}
                        refElems={refElems}
                        visibleElement={visibleElem}
                        changeVisibility={changeVisibility}
                        changeModalTab={changeModalTab}
                        hasProperties={hasProperties}
                        hasDescription={hasDescription}
                        shouldMapBeRendered={!modalIsOpen}
                    />
                ) : (
                    <OfferDetailLimited
                        offer={offer}
                        refElems={refElems}
                        variables={variables}
                        changeModalTab={changeModalTab}
                        hasDescription={hasDescription}
                        shouldMapBeRendered={!modalIsOpen}
                    />
                )}

                {articles.length > 0 && <RelatedArticlesSection heading="Artykuły powiązane:" articles={articles} />}

                <SchemaOfferDetail offer={offer} />

                {!isSold && offer.is_active && <SchemaOfferDetailRating offer={offer} />}

                {/* For now, we don't want to show abroad regions on offers listing view - so we don't want to show links to listings */}
                {offer && offer.region.country === Country.POLAND && (
                    <OfferLocationLinksWrapper className={linksWrapperStyle}>
                        <LocationLinks recommendedRegions={recommendedRegions} offerType={offer.type} region={offer.region} relatedOffers={relatedOffers} />
                    </OfferLocationLinksWrapper>
                )}
            </article>

            {/** View Modals */}
            <FavouritesInfoModal />

            <LeadModal />

            {isMounted && <HotlineChatBotModal hotlineNumber={HOTLINE_CHATBOT_OFFER_PHONE_NUMBER} hotlineModalTriggerId={HOTLINE_CHATBOT_MODAL_TRIGGER_ID} />}

            {!isArchived && <HotlineModal />}

            {rpFeatureFlagVendorReportDownload && !isAbroad && <VendorDetailReportModal />}
        </>
    );
};

export const OfferDetailView = connect<IStateProps, IActionsProps, IOwnProps, IRPStore>(mapStateToProps, mapActionsToProps)(OfferDetailViewC);

/**
 * Connect
 */
function mapStateToProps(state: IRPStore): IStateProps {
    return {
        offer: state.offerDetail.offer,
        articles: state.offerDetail.articles,
        promotions: state.offerDetail.promotions,
        variables: state.variables && state.variables.data,
        recommendedRegions: state.offerDetail.recommendedRegions,
        relatedOffers: state.offerDetail.relatedOffers
    };
}

function mapActionsToProps(dispatch: Dispatch): IActionsProps {
    return {
        ...bindActionCreators({backToUrl}, dispatch)
    };
}

// Sub components
interface INearestOffersInfoProps {
    friendlyOfferListLink: string;
    regionStatsForSale?: number | false;
    currentStageOfferCount?: number | null;
}

function NearestOffersInfo(props: INearestOffersInfoProps) {
    const {regionStatsForSale, friendlyOfferListLink, currentStageOfferCount} = props;

    const saleStats = currentStageOfferCount ? currentStageOfferCount : regionStatsForSale;

    if (!saleStats) {
        return;
    }

    return (
        <Text variant="body_copy_2" className={css({pt: 2})}>
            {`Mamy dla Ciebie `}
            <a className={noWrapUnderlinedStyle} href={friendlyOfferListLink}>
                {saleStats} {offerPlural(saleStats)}
            </a>

            {currentStageOfferCount ? ` w kolejnym etapie tej inwestycji!` : " w najbliższej okolicy!"}
        </Text>
    );
}

function InactiveInfo() {
    return (
        <Text variant="body_copy_2">
            Uwaga! Wizytówka niniejszej inwestycji jest nieaktywna. Informacje na jej temat pomagają nam jednak tworzyć wiarygodną bazę informacji na temat
            lokalnego rynku nieruchomości.
        </Text>
    );
}

//  Styles

const headerWrapper = css({
    my: 2,

    lg: {
        mt: 3,
        mb: 2
    }
});

const linksWrapperStyle = css({
    p: 0,
    backgroundColor: "#fff",

    md: {
        py: "6.4rem !important"
    }
});

const mainMessageWrap = css({
    mb: 2,
    borderRadius: 2,
    p: 2,
    background: "#fff",
    boxShadow: "var(--shadow.0)"
});

const mainMessageTitleStyle = css({
    mb: 2
});

const noWrapUnderlinedStyle = css({
    textWrap: "unset",
    textDecoration: "underline"
});

const mapWrapper = css({
    mb: 3,
    position: "relative",
    width: "100%",
    height: "500px",

    sm: {
        height: "600px"
    }
});

const planner3DStyle = css({
    display: "inline-block"
});

const mobileIconButtonsWrapper = css({
    display: "flex",
    flexWrap: "wrap",
    columnGap: "2.7rem",
    rowGap: 2,
    mb: 2
});
